import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import MarkUsedModal from '../components/MarkUsedModal';
import RestockPickerModal from '../components/RestockPickerModal';
import './V9ActionQueuePage.css';

async function parseJsonSafe(res) {
  try {
    return await res.json();
  } catch (_) {
    return null;
  }
}

function renderQueueAvailable(row) {
  const allocationMode = String(row?.meta?.allocationMode || '').trim().toUpperCase();
  const componentSummary = Array.isArray(row?.meta?.componentSummary) ? row.meta.componentSummary : [];
  if (allocationMode === 'COMPONENT_STOCK' && componentSummary.length > 0) {
    return (
      <div className="v9queue-component-available">
        <strong>Components</strong>
        {componentSummary.slice(0, 3).map((component) => (
          <small key={String(component?.componentSku || '')}>
            {String(component?.componentSku || '-')} {Number(component?.availableQty || 0).toFixed(2)}/{Number(component?.requiredQty || 0).toFixed(2)}m
          </small>
        ))}
        {componentSummary.length > 3 && <small>+{componentSummary.length - 3} more</small>}
      </div>
    );
  }
  return Number(row?.availableQty || 0).toFixed(2);
}

function V9ActionQueuePage() {
  const { currentUser, canPerformAction } = useAuth();
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState('');
  const [markModalOpen, setMarkModalOpen] = useState(false);
  const [markModalRow, setMarkModalRow] = useState(null);
  const [markCandidates, setMarkCandidates] = useState(null);
  const [markLoading, setMarkLoading] = useState(false);
  const [markSubmitting, setMarkSubmitting] = useState(false);
  const [packModalOpen, setPackModalOpen] = useState(false);
  const [packRow, setPackRow] = useState(null);
  const [packPartner, setPackPartner] = useState('delhivery');
  const [packAwb, setPackAwb] = useState('');
  const [packSubmitting, setPackSubmitting] = useState(false);
  const [restockPickerOpen, setRestockPickerOpen] = useState(false);
  const [restockPickerCtx, setRestockPickerCtx] = useState({ sku: '', orderId: '', orderNumber: '', productImage: '', productName: '' });
  const [pillCounts, setPillCounts] = useState({});
  const [pillTotalOpen, setPillTotalOpen] = useState(0);
  const [dedupGroups, setDedupGroups] = useState(null);
  const [dedupScanning, setDedupScanning] = useState(false);
  const [dedupMerging, setDedupMerging] = useState(false);
  const [backfillReceiveRunning, setBackfillReceiveRunning] = useState(false);
  const [repairShippedRunning, setRepairShippedRunning] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const autoRefreshIntervalRef = useRef(null);
  const hiddenLegacyTypes = useMemo(() => new Set(['PROCESS_BY_PO_ELIGIBLE', 'READY_TO_PACK']), []);

  const actionPills = useMemo(() => ([
    { value: 'ALL', label: 'All' },
    { value: 'MARK_USED_ELIGIBLE', label: 'Mark Used' },
    { value: 'PACK_ELIGIBLE', label: 'Pack' },
    { value: 'INVOICE_REVIEW_REQUIRED', label: 'Invoice Review' },
    { value: 'RESTOCK_PENDING', label: 'Restock' },
    { value: 'EXCESS_RESTOCK_PENDING', label: 'Excess Restock' },
    { value: 'RETURN_RECEIVE_PENDING', label: 'Return Receive' },
    { value: 'PAYMENT_RECONCILE_HOLD', label: 'Payment Hold' },
  ]), []);
  const [actionType, setActionType] = useState('MARK_USED_ELIGIBLE');

  const authedFetch = useCallback(async (url, options = {}) => {
    const token = await currentUser.getIdToken();
    const hasBody = options && Object.prototype.hasOwnProperty.call(options, 'body');
    const contentType = hasBody ? { 'Content-Type': 'application/json' } : {};
    return fetch(url, {
      ...options,
      headers: {
        ...contentType,
        Authorization: `Bearer ${token}`,
        ...((options && options.headers) || {}),
      },
    });
  }, [currentUser]);

  const buildUrl = useCallback((cursor = '') => {
    const params = new URLSearchParams();
    params.set('limit', '50');
    if (actionType && actionType !== 'ALL') params.set('actionType', actionType);
    params.set('status', 'OPEN');
    if (cursor) params.set('cursor', cursor);
    return `/api/v9/action-queue?${params.toString()}`;
  }, [actionType]);

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    setMsg('Loading queue...');
    try {
      const res = await authedFetch(buildUrl());
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setMsg(data?.error?.message || 'Failed to load action queue.');
        setRows([]);
        setHasMore(false);
        setNextCursor('');
        return;
      }
      const items = Array.isArray(data?.result?.items) ? data.result.items : [];
      setRows(items);
      setHasMore(data?.result?.hasMore === true);
      setNextCursor(String(data?.result?.nextCursor || ''));
      setLastRefreshedAt(Date.now());
      setMsg(items.length === 0 ? 'No queue rows found.' : `Loaded ${items.length} queue rows.`);
    } finally {
      setLoading(false);
    }
  }, [authedFetch, buildUrl]);

  const loadPillCounts = useCallback(async () => {
    try {
      const res = await authedFetch('/api/v9/action-queue/counts?status=OPEN');
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) return;
      const serverCounts = data?.result?.countsByActionType || {};
      const allowedCounts = {};
      actionPills.forEach((pill) => {
        if (pill.value === 'ALL') return;
        allowedCounts[pill.value] = Number(serverCounts?.[pill.value] || 0);
      });
      setPillCounts(allowedCounts);
      const sumAllowed = Object.values(allowedCounts).reduce((s, n) => s + Number(n || 0), 0);
      setPillTotalOpen(sumAllowed);
    } catch (_) {
      // keep previous counts on transient failures
    }
  }, [actionPills, authedFetch]);

  const loadMore = useCallback(async () => {
    if (!hasMore || !nextCursor) return;
    setLoadingMore(true);
    try {
      const res = await authedFetch(buildUrl(nextCursor));
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setMsg(data?.error?.message || 'Failed to load more queue rows.');
        return;
      }
      const items = Array.isArray(data?.result?.items) ? data.result.items : [];
      setRows((prev) => [...prev, ...items]);
      setHasMore(data?.result?.hasMore === true);
      setNextCursor(String(data?.result?.nextCursor || ''));
    } finally {
      setLoadingMore(false);
    }
  }, [authedFetch, buildUrl, hasMore, nextCursor]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  useEffect(() => {
    loadPillCounts();
  }, [loadPillCounts]);

  // ── Auto-refresh every 60 s (pauses when tab hidden) ──
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      loadFirstPage();
      loadPillCounts();
    };
    autoRefreshIntervalRef.current = setInterval(tick, 60_000);
    return () => clearInterval(autoRefreshIntervalRef.current);
  }, [loadFirstPage, loadPillCounts]);

  // Refresh immediately when user switches back to tab after ≥60 s
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const staleSec = lastRefreshedAt ? (Date.now() - lastRefreshedAt) / 1000 : Infinity;
      if (staleSec >= 60) {
        loadFirstPage();
        loadPillCounts();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [loadFirstPage, loadPillCounts, lastRefreshedAt]);

  const lastUpdatedLabel = useMemo(() => {
    if (!lastRefreshedAt) return '';
    const d = new Date(lastRefreshedAt);
    return `Updated ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }, [lastRefreshedAt]);

  const recomputeQueue = useCallback(async () => {
    const body = { limit: 5000 };
    const ok = window.confirm('Recompute queue for all v9 orders now?');
    if (!ok) return;
    setLoading(true);
    setMsg('Recomputing queue for all v9 orders...');
    try {
      const res = await authedFetch('/api/v9/action-queue/recompute', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setMsg(data?.error?.message || 'Queue recompute failed.');
        return;
      }
      setMsg(`Queue recomputed for ${Number(data?.result?.ordersScanned || 0)} orders.`);
      await loadFirstPage();
      await loadPillCounts();
    } finally {
      setLoading(false);
    }
  }, [authedFetch, loadFirstPage, loadPillCounts]);

  const backfillV2BatchReceive = useCallback(async () => {
    const ok = window.confirm('Backfill V2 batch receives into V9 lines?\n\nThis updates receivedQty on V9 order lines for orders that were received via V2 batches. Safe to run multiple times.');
    if (!ok) return;
    setBackfillReceiveRunning(true);
    setMsg('Backfilling V2 batch receives into V9...');
    try {
      const res = await authedFetch('/api/v9/admin/backfill-v2-batch-receive-to-v9', {
        method: 'POST',
        body: JSON.stringify({ limit: 500 }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setMsg(data?.error?.message || 'Backfill failed.');
        return;
      }
      const s = data.stats || {};
      setMsg(`Backfill done. Batches scanned: ${s.batchesScanned}, V9 orders updated: ${s.v9OrdersFound}, lines updated: ${s.v9LineUpdates}${s.errors?.length ? `, errors: ${s.errors.length}` : ''}.`);
      await loadPillCounts();
    } finally {
      setBackfillReceiveRunning(false);
    }
  }, [authedFetch, loadPillCounts]);

  const visibleRows = useMemo(
    () => rows.filter((r) => !hiddenLegacyTypes.has(String(r?.actionType || '').trim().toUpperCase())),
    [hiddenLegacyTypes, rows]
  );
  const openCount = useMemo(() => visibleRows.filter((r) => String(r.status || '').toUpperCase() === 'OPEN').length, [visibleRows]);
  const getPillCount = useCallback((pillValue) => {
    if (pillValue === 'ALL') return Number(pillTotalOpen || 0);
    return Number(pillCounts?.[pillValue] || 0);
  }, [pillCounts, pillTotalOpen]);
  const visibleActionPills = useMemo(
    () => actionPills.filter((pill) => pill.value === 'ALL' || getPillCount(pill.value) > 0),
    [actionPills, getPillCount]
  );

  const openMarkUsedModal = useCallback(async (row) => {
    if (!row?.orderId || !row?.sku) return;
    setMarkModalRow(row);
    setMarkModalOpen(true);
    setMarkLoading(true);
    try {
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(row.orderId)}/lines/${encodeURIComponent(row.sku)}/stock-candidates`);
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setMsg(data?.error?.message || 'Failed to load stock candidates.');
        setMarkCandidates(null);
        return;
      }
      setMarkCandidates(data.result || null);
    } finally {
      setMarkLoading(false);
    }
  }, [authedFetch]);

  const closeMarkUsedModal = useCallback(() => {
    setMarkModalOpen(false);
    setMarkModalRow(null);
    setMarkCandidates(null);
    setMarkLoading(false);
    setMarkSubmitting(false);
  }, []);
  const submitMarkUsed = useCallback(async (consume) => {
    if (!markModalRow?.orderId || !markModalRow?.sku) return;
    setMarkSubmitting(true);
    try {
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(markModalRow.orderId)}/mark-used`, {
        method: 'POST',
        body: JSON.stringify({
          items: [{ sku: String(markModalRow.sku || '').trim().toUpperCase(), consume }],
        }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setMsg(data?.error?.message || 'Mark Used failed.');
        return;
      }
      setMsg('Marked used from queue.');
      closeMarkUsedModal();
      await loadFirstPage();
      await loadPillCounts();
    } finally {
      setMarkSubmitting(false);
    }
  }, [authedFetch, closeMarkUsedModal, loadFirstPage, loadPillCounts, markModalRow]);

  const openPackModal = useCallback((row) => {
    if (!row?.orderId) return;
    setPackRow(row);
    setPackPartner('delhivery');
    setPackAwb('');
    setPackModalOpen(true);
  }, []);

  const closePackModal = useCallback(() => {
    if (packSubmitting) return;
    setPackModalOpen(false);
    setPackRow(null);
    setPackAwb('');
    setPackPartner('delhivery');
  }, [packSubmitting]);

  const submitPack = useCallback(async () => {
    if (!packRow?.orderId || !String(packAwb || '').trim()) return;
    setPackSubmitting(true);
    try {
      const detailRes = await authedFetch(`/api/v9/orders/${encodeURIComponent(packRow.orderId)}`);
      const detailData = await parseJsonSafe(detailRes);
      if (!detailRes.ok || !detailData?.ok) {
        setMsg(detailData?.error?.message || 'Failed to load order lines for packing.');
        return;
      }
      const orderLines = Array.isArray(detailData?.result?.lines) ? detailData.result.lines : [];
      if (orderLines.length === 0) {
        setMsg('No order lines found for packing.');
        return;
      }
      const lineItems = orderLines
        .map((line) => ({
          sku: String(line?.sku || '').trim().toUpperCase(),
          qty: Number(line?.netRequiredQty || 0),
        }))
        .filter((x) => x.sku && x.qty > 0);
      if (lineItems.length === 0) {
        setMsg('No packable line items found.');
        return;
      }

      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(packRow.orderId)}/pack`, {
        method: 'POST',
        body: JSON.stringify({
          shipmentPartner: String(packPartner || 'delhivery').toLowerCase(),
          awb: String(packAwb || '').trim(),
          packages: [{
            packageRef: `PKG-${Date.now()}`,
            lineItems,
          }],
        }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setMsg(data?.error?.message || 'Pack failed.');
        return;
      }
      setMsg('Order packed from queue.');
      closePackModal();
      await loadFirstPage();
      await loadPillCounts();
    } finally {
      setPackSubmitting(false);
    }
  }, [authedFetch, closePackModal, loadFirstPage, loadPillCounts, packAwb, packPartner, packRow]);

  const refreshInvoiceFromQueue = useCallback(async (row) => {
    const oid = String(row?.orderId || '').trim();
    if (!oid) return;
    setLoading(true);
    setMsg('Refreshing invoice from Zoho...');
    try {
      const res = await authedFetch(`/api/v9/orders/${encodeURIComponent(oid)}/invoice/refresh`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setMsg(data?.error?.message || 'Invoice refresh failed.');
        return;
      }
      setMsg('Invoice refreshed from queue.');
      await loadFirstPage();
      await loadPillCounts();
    } finally {
      setLoading(false);
    }
  }, [authedFetch, loadFirstPage, loadPillCounts]);

  // B3 Stage 2 — open the unified piece-level restock picker for a queue row (Restock,
  // Excess Restock, and Undo all route here; the modal shows restockable pieces + an
  // "Already restocked" undo section, scoped to the row's SKU).
  const openRestockPicker = useCallback((row) => {
    setRestockPickerCtx({
      sku: String(row?.sku || '').trim().toUpperCase(),
      orderId: String(row?.orderId || '').trim(),
      orderNumber: String(row?.orderNumber || row?.orderId || '').trim(),
      productImage: String(row?.productImageUrl || '').trim(),
      productName: String(row?.productName || '').trim(),
    });
    setRestockPickerOpen(true);
  }, []);

  const closeRestockPicker = useCallback(() => {
    setRestockPickerOpen(false);
    setRestockPickerCtx({ sku: '', orderId: '', orderNumber: '', productImage: '', productName: '' });
  }, []);

  const repairV2ShippedOrders = useCallback(async () => {
    const ok = window.confirm('Repair V2-shipped orders in V9?\n\nThis scans V9 orders stuck in ALLOCATING/PRODUCTION/READY_TO_PACK and advances them to IN_TRANSIT or DELIVERED based on their V2 shipment status.');
    if (!ok) return;
    setRepairShippedRunning(true);
    setMsg('Repairing V2-shipped orders in V9...');
    try {
      const res = await authedFetch('/api/v9/admin/repair-v2-shipped-orders', {
        method: 'POST',
        body: JSON.stringify({ limit: 500 }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setMsg(data?.error?.message || 'Repair failed.');
        return;
      }
      const s = data.stats || {};
      setMsg(`Repair done. Scanned: ${s.scanned}, repaired: ${s.repaired}, not shipped: ${s.skippedNotShipped}, no V2 order: ${s.skippedNoV2Order}${s.errors?.length ? `, errors: ${s.errors.length}` : ''}.`);
      await loadFirstPage();
      await loadPillCounts();
    } finally {
      setRepairShippedRunning(false);
    }
  }, [authedFetch, loadFirstPage, loadPillCounts]);

  const scanDuplicates = useCallback(async () => {
    setDedupScanning(true);
    setDedupGroups(null);
    setMsg('Scanning for duplicate V9 orders...');
    try {
      const res = await authedFetch('/api/v9/admin/dedup-orders/scan?limit=5000');
      const data = await parseJsonSafe(res);
      if (!res.ok || !data?.ok) {
        setMsg(data?.error?.message || 'Dedup scan failed.');
        return;
      }
      setDedupGroups(data.duplicateGroups || []);
      const n = (data.duplicateGroups || []).length;
      setMsg(n === 0
        ? `✓ No duplicates found in ${data.scanned} orders scanned.`
        : `Found ${n} duplicate group(s) across ${data.scanned} orders scanned.`);
    } finally {
      setDedupScanning(false);
    }
  }, [authedFetch]);

  const mergeAllDuplicates = useCallback(async () => {
    if (!dedupGroups || dedupGroups.length === 0) return;
    const ok = window.confirm(`Merge ${dedupGroups.length} duplicate group(s)? This will delete duplicate order docs and move their lines into the canonical doc.`);
    if (!ok) return;
    setDedupMerging(true);
    setMsg('Merging duplicates...');
    try {
      // Send in batches of 20
      const BATCH = 20;
      const groups = dedupGroups.map(g => ({ keepId: g.keepId, deleteIds: g.deleteIds }));
      let merged = 0, errors = 0;
      for (let i = 0; i < groups.length; i += BATCH) {
        const chunk = groups.slice(i, i + BATCH);
        const res = await authedFetch('/api/v9/admin/dedup-orders/merge', {
          method: 'POST',
          body: JSON.stringify({ groups: chunk }),
        });
        const data = await parseJsonSafe(res);
        if (!res.ok || !data?.ok) {
          errors++;
          continue;
        }
        (data.report || []).forEach(r => { if (r.ok) merged++; else errors++; });
      }
      setDedupGroups(null);
      setMsg(`Done. ${merged} group(s) merged${errors > 0 ? `, ${errors} error(s).` : '.'}`);
    } finally {
      setDedupMerging(false);
    }
  }, [authedFetch, dedupGroups]);

  return (
    <div className="v9queue-page">
      <header className="v9queue-header">
        <div className="v9queue-header-top">
          <div className="v9queue-header-title">
            <h1>V9 Action Queue</h1>
            {lastUpdatedLabel && <span className="v9queue-updated-label">{lastUpdatedLabel}</span>}
          </div>
          <div className="v9queue-header-actions">
            <button
              className="v9queue-recompute-btn v9queue-refresh-btn"
              onClick={() => { loadFirstPage(); loadPillCounts(); }}
              disabled={loading}
              title="Refresh queue"
            >
              <span className={`v9queue-refresh-icon${loading ? ' spinning' : ''}`}>↻</span>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              className="v9queue-recompute-btn"
              onClick={recomputeQueue}
              disabled={loading || loadingMore}
            >
              Recompute Queue
            </button>
            {canPerformAction('admin.reconcile') && (
              <button
                className="v9queue-recompute-btn"
                onClick={scanDuplicates}
                disabled={dedupScanning || dedupMerging}
              >
                {dedupScanning ? 'Scanning…' : 'Scan Duplicates'}
              </button>
            )}
            {canPerformAction('admin.reconcile') && (
              <button
                className="v9queue-recompute-btn"
                onClick={backfillV2BatchReceive}
                disabled={backfillReceiveRunning}
              >
                {backfillReceiveRunning ? 'Backfilling…' : 'Backfill V2 Receives'}
              </button>
            )}
            {canPerformAction('admin.reconcile') && (
              <button
                className="v9queue-recompute-btn"
                onClick={repairV2ShippedOrders}
                disabled={repairShippedRunning}
              >
                {repairShippedRunning ? 'Repairing…' : 'Repair Shipped Orders'}
              </button>
            )}
            {canPerformAction('admin.reconcile') && dedupGroups && dedupGroups.length > 0 && (
              <button
                className="v9queue-recompute-btn v9queue-recompute-btn--danger"
                onClick={mergeAllDuplicates}
                disabled={dedupMerging || dedupScanning}
              >
                {dedupMerging ? 'Merging…' : `Merge ${dedupGroups.length} Duplicate(s)`}
              </button>
            )}
          </div>
        </div>
        {dedupGroups && dedupGroups.length > 0 && (
          <div className="v9queue-dedup-panel">
            <table className="v9queue-dedup-table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Type</th>
                  <th>Keep</th>
                  <th>Delete</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {dedupGroups.map((g, i) => (
                  <tr key={i}>
                    <td>{g.groupKey}</td>
                    <td>{g.groupType === 'shopifyOrderName' ? 'Shopify Name' : 'Zoho Ref'}</td>
                    <td className="v9queue-dedup-keep">{g.keepId}</td>
                    <td className="v9queue-dedup-delete">{g.deleteIds.join(', ')}</td>
                    <td>{g.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </header>

      <section className="v9queue-pillbar">
        {visibleActionPills.map((pill) => (
          <button
            key={pill.value}
            className={`v9queue-pill ${actionType === pill.value ? 'active' : ''}`}
            onClick={() => setActionType(pill.value)}
            disabled={loading}
          >
            {pill.label} ({getPillCount(pill.value)})
          </button>
        ))}
      </section>

      <section className="v9queue-summary">
        <div><strong>Rows:</strong> {visibleRows.length}</div>
        <div><strong>Open Rows:</strong> {openCount}</div>
        <div><strong>Status:</strong> {msg}</div>
      </section>

      <section className="v9queue-table-wrap">
        <table className="v9queue-table">
          <thead>
            <tr>
              <th>Image</th>
              <th>Order</th>
              <th>SKU</th>
              <th>Pending</th>
              <th>Available</th>
              <th>Status</th>
              <th>Updated</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={row.id}>
                <td>
                  {String(row.productImageUrl || '').trim() ? (
                    <img
                      className="v9queue-line-thumb"
                      src={row.productImageUrl}
                      alt={row.sku || 'Product'}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="v9queue-line-thumb-fallback">No image</div>
                  )}
                </td>
                <td>
                  <div>{row.orderNumber || '-'}</div>
                  <small>{row.orderId || '-'}</small>
                </td>
                <td>{row.sku || '-'}</td>
                <td>{Number(row.pendingQty || 0).toFixed(2)}</td>
                <td>{renderQueueAvailable(row)}</td>
                <td>{row.status || '-'}</td>
                <td>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}</td>
                <td>
                  <div className="v9queue-actions">
                    {canPerformAction('v9ActionQueue.markUsed')
                      && String(row.actionType || '').toUpperCase() === 'MARK_USED_ELIGIBLE'
                      && String(row.status || '').toUpperCase() === 'OPEN'
                      && row.orderId && row.sku && (
                        <button className="v9queue-inline-btn" onClick={() => openMarkUsedModal(row)}>Mark Used</button>
                      )}
                    {canPerformAction('v9ActionQueue.pack')
                      && String(row.actionType || '').toUpperCase() === 'PACK_ELIGIBLE'
                      && String(row.status || '').toUpperCase() === 'OPEN'
                      && row.orderId && (
                        <button className="v9queue-inline-btn" onClick={() => openPackModal(row)}>Pack</button>
                      )}
                    {canPerformAction('v9ActionQueue.invoiceRefresh')
                      && String(row.actionType || '').toUpperCase() === 'INVOICE_REVIEW_REQUIRED'
                      && String(row.status || '').toUpperCase() === 'OPEN'
                      && row.orderId && (
                        <button className="v9queue-inline-btn" onClick={() => refreshInvoiceFromQueue(row)}>Refresh Invoice</button>
                      )}
                    {canPerformAction('v9ActionQueue.excessRestock')
                      && String(row.actionType || '').toUpperCase() === 'EXCESS_RESTOCK_PENDING'
                      && String(row.status || '').toUpperCase() === 'OPEN'
                      && row.orderId && row.sku && (
                        <button className="v9queue-inline-btn" onClick={() => openRestockPicker(row)}>Restock</button>
                      )}
                    {canPerformAction('v9ActionQueue.restock')
                      && String(row.actionType || '').toUpperCase() === 'RESTOCK_PENDING'
                      && String(row.status || '').toUpperCase() === 'OPEN'
                      && row.orderId && row.sku && (
                        <button className="v9queue-inline-btn" onClick={() => openRestockPicker(row)}>Restock</button>
                      )}
                    {canPerformAction('v9ActionQueue.undoRestock')
                      && String(row.actionType || '').toUpperCase() === 'UNDO_RESTOCK_ELIGIBLE'
                      && String(row.status || '').toUpperCase() === 'OPEN'
                      && row.orderId && row.sku && (
                        <button className="v9queue-inline-btn v9queue-inline-btn--danger" onClick={() => openRestockPicker(row)}>
                          Undo Restock
                        </button>
                      )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && visibleRows.length === 0 && (
              <tr>
                <td colSpan="8" className="v9queue-empty">No queue rows.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="v9queue-footer">
        <button onClick={loadMore} disabled={!hasMore || loadingMore || loading}>
          {loadingMore ? 'Loading...' : (hasMore ? 'Load More' : 'No More Rows')}
        </button>
      </section>

      <MarkUsedModal
        isOpen={markModalOpen}
        loading={markLoading}
        submitting={markSubmitting}
        context={{
          sku: markModalRow?.sku || '',
          orderNumber: markModalRow?.orderNumber || '',
          orderId: markModalRow?.orderId || '',
          pendingQty: Number(markCandidates?.pendingQty || markModalRow?.pendingQty || 0),
          orderedQty: Number(markCandidates?.pendingQty || markModalRow?.pendingQty || 0),
          receivedQty: 0,
          productName: markCandidates?.productName || markModalRow?.sku || '',
          productImage: markCandidates?.productImageUrl || '',
          allocationMode: String(markCandidates?.allocationMode || markModalRow?.meta?.allocationMode || '').trim().toUpperCase(),
          componentSummary: Array.isArray(markCandidates?.componentSummary) ? markCandidates.componentSummary : [],
          componentCandidates: Array.isArray(markCandidates?.componentCandidates) ? markCandidates.componentCandidates : [],
        }}
        pieces={markCandidates?.pieces || []}
        onClose={closeMarkUsedModal}
        onSubmit={submitMarkUsed}
      />

      {packModalOpen && (
        <div className="v9queue-modal-overlay" onClick={closePackModal}>
          <div className="v9queue-pack-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Mark Pack</h3>
            <div className="v9queue-modal-sub">Order: {String(packRow?.orderNumber || packRow?.orderId || '-').trim()}</div>
            <div className="v9queue-pack-fields">
              <select value={packPartner} onChange={(e) => setPackPartner(e.target.value)} disabled={packSubmitting}>
                <option value="delhivery">Delhivery</option>
                <option value="shiprocket">Shiprocket</option>
              </select>
              <input
                value={packAwb}
                onChange={(e) => setPackAwb(e.target.value)}
                placeholder="AWB"
                disabled={packSubmitting}
              />
            </div>
            <div className="v9queue-modal-actions">
              <button onClick={closePackModal} disabled={packSubmitting}>Cancel</button>
              <button onClick={submitPack} disabled={packSubmitting || !String(packAwb || '').trim()}>
                {packSubmitting ? 'Packing...' : 'Confirm Pack'}
              </button>
            </div>
          </div>
        </div>
      )}

      <RestockPickerModal
        open={restockPickerOpen}
        orderId={restockPickerCtx.orderId}
        sku={restockPickerCtx.sku}
        orderNumber={restockPickerCtx.orderNumber}
        productImage={restockPickerCtx.productImage}
        productName={restockPickerCtx.productName}
        authedFetch={authedFetch}
        onClose={closeRestockPicker}
        onDone={async () => { await loadFirstPage(); await loadPillCounts(); }}
      />


    </div>
  );
}

export default V9ActionQueuePage;
